import {
    ParamType,
    ERPConnectionParam,
    ERPConnectionInfo,
} from '../../../services/backendServices/ViewModels/ERPConnectionInfoViewModel';
import { date as dateUtils } from '../../../utils/date';

/**
 * Utility function to normalize Parameter field to always be an array
 * This handles the XML-to-JSON conversion issue where single items become objects instead of arrays
 */
export function normalizeERPConnectionInfo(connectionInfo: any): ERPConnectionInfo {
    if (!connectionInfo?.root?.Parameters?.Parameter) {
        throw new Error('Invalid ERP connection info structure');
    }

    // Ensure Parameter is always an array
    const parameters = connectionInfo.root.Parameters.Parameter;
    const normalizedParameters = Array.isArray(parameters) ? parameters : [parameters];

    return {
        root: {
            Parameters: {
                Parameter: normalizedParameters,
            },
        },
    };
}

/**
 * Convert ERPConnectionInfo.root.Parameters.Parameter[] (plain object array) to ERPConnectionParam[]
 * Assumes parameterArray is already normalized (i.e., always an array)
 */
export function convertERPConnectionInfoToParams(parameterArray: Array<Record<string, any>>): ERPConnectionParam[] {
    return parameterArray.map((param) => {
        let value: string | null;
        let defaultValue: string;

        // logic to properly handle null/undefined values
        if (param.Value !== undefined) {
            if (param.Value === null) {
                value = null;
            } else {
                value = String(param.Value);
            }
        } else if (param.Default !== undefined && param.Default !== null) {
            value = String(param.Default);
        } else {
            value = '';
        }

        if (param.Default !== undefined && param.Default !== null) {
            defaultValue = String(param.Default);
        } else {
            defaultValue = '';
        }

        // Special handling for Time type: only provide a valid time string if both Value and Default are undefined/empty
        if (param.Type === ParamType.Time) {
            if (param.Value === undefined && (!defaultValue || defaultValue === '')) {
                value = '00:00:00';
            }
            if (!defaultValue || defaultValue === '') {
                defaultValue = '00:00:00';
            }
        }

        return {
            Name: param.Name,
            Label: param.Label,
            Description: param.Description,
            Type: param.Type as ParamType,
            Value: value,
            Default: defaultValue,
            EnumOptions: param.EnumOptions,
            Required: param.Required,
        };
    });
}

/**
 * Convert a full ERPConnectionInfo object to ERPConnectionParam[]
 * This function handles the normalization of the Parameter field and then converts to params
 */
export function convertERPConnectionInfoObjectToParams(connectionInfo: ERPConnectionInfo): ERPConnectionParam[] {
    const normalizedInfo = normalizeERPConnectionInfo(connectionInfo);

    return convertERPConnectionInfoToParams(normalizedInfo.root.Parameters.Parameter);
}

/**
 * Convert raw parameter data (which might be a single object or array) to ERPConnectionParam[]
 * This handles the XML-to-JSON conversion issue at the parameter level
 */
export function convertRawParametersToParams(rawParameters: any): ERPConnectionParam[] {
    const normalizedArray = Array.isArray(rawParameters) ? rawParameters : [rawParameters];

    return convertERPConnectionInfoToParams(normalizedArray);
}

export function convertParamsToERPConnectionInfo(params: ERPConnectionParam[]): ERPConnectionInfo {
    const parameterArray = params.map((param) => {
        let value: string | null = null;
        if (param.Value !== undefined && param.Value !== null) {
            value = String(param.Value);
        } else if (param.Value === null) {
            value = null;
        }
        return {
            Name: param.Name ?? '',
            Label: param.Label ?? '',
            Description: param.Description ?? '',
            Type: param.Type,
            Default: param.Default ?? '',
            Value: value,
            EnumOptions: param.EnumOptions,
            Required: param.Required ?? '',
        };
    });
    return {
        root: {
            Parameters: {
                Parameter: parameterArray,
            },
        },
    };
}

/**
 * Merges a base ERPConnectionInfo (source of truth for structure) with a config ERPConnectionInfo (preserving values where possible).
 * New fields from base are added, removed fields are dropped, and values from config are preserved if the field exists in both.
 * Always returns a valid ERPConnectionInfo object.
 */
export function mergeERPConnectionInfo(
    base: ERPConnectionInfo | null,
    config: ERPConnectionInfo | null
): ERPConnectionInfo {
    // Default empty structure if both are null/undefined
    const emptyConnectionInfo: ERPConnectionInfo = { root: { Parameters: { Parameter: [] } } };

    if (!base && !config) {
        return emptyConnectionInfo;
    }
    if (!base) {
        return config || emptyConnectionInfo;
    }
    if (!config) {
        return base;
    }

    // Use the raw parameter converter to handle potential single object vs array issues
    const baseParams = convertRawParametersToParams(base.root?.Parameters?.Parameter ?? []);
    const configParams = convertRawParametersToParams(config.root?.Parameters?.Parameter ?? []);

    // Map config params by Name for quick lookup
    const configParamMap = new Map(configParams.map((param) => [param.Name, param]));

    // Merge: for each base param, preserve value from config if present
    const mergedParams = baseParams.map((baseParam) => {
        const configParam = configParamMap.get(baseParam.Name);
        return {
            ...baseParam,
            Value: configParam && configParam.Value != null ? configParam.Value : baseParam.Value,
        };
    });

    return convertParamsToERPConnectionInfo(mergedParams);
}

/**
 * Converts a string (from DB/XML) to a valid Date object or null.
 */
export function parseDateString(value: string | null | undefined): Date | null {
    if (!value) {
        return null;
    }
    const d = new Date(dateUtils.tzNormalization.normalizeDateTimeString(value));
    return !isNaN(d.getTime()) ? d : null;
}

/**
 * Converts a Date object to a storage string (YYYY-MM-DD) or '' if invalid/cleared.
 */
export function dateToStorageString(date: Date | null | undefined): string {
    if (date instanceof Date && !isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
    }
    return '';
}

/**
 * Constants for QuickBooks parameter names. All lowercase for consistency.
 */
export const QUICKBOOKS_PARAM_NAMES = {
    CLIENT_ID: 'clientid',
    CLIENT_SECRET: 'clientsecret',
    REDIRECT_URI: 'redirecturi',
    CONNECTED: 'qboconnected',
    ENVIRONMENT: 'environment',
    REALM_ID: 'realmid',
    REFRESH_TOKEN: 'refreshtoken',
} as const;

export const QUICKBOOKS_ERP_NAME = 'QuickBooksOnline';

/**
 * Extract QuickBooks configuration from ERPConnectionInfo parameters
 * Returns null if required parameters are missing
 */
export function extractQuickBooksConfig(connectionInfo: ERPConnectionInfo | null): {
    clientID: string;
    clientSecret: string;
    redirectUri: string;
} | null {
    if (!connectionInfo) {
        return null;
    }

    const params = convertRawParametersToParams(connectionInfo.root?.Parameters?.Parameter ?? []);
    const paramMap = new Map(params.map((p) => [p.Name.toLowerCase(), p.Value || p.Default || '']));

    const clientID = paramMap.get(QUICKBOOKS_PARAM_NAMES.CLIENT_ID) || '';
    const clientSecret = paramMap.get(QUICKBOOKS_PARAM_NAMES.CLIENT_SECRET) || '';
    const redirectUri = paramMap.get(QUICKBOOKS_PARAM_NAMES.REDIRECT_URI) || '';

    // Return null if any required field is missing
    if (!clientID || !clientSecret || !redirectUri) {
        return null;
    }

    return {
        clientID,
        clientSecret,
        redirectUri,
    };
}

/**
 * Extract QuickBooks connection status from ERPConnectionInfo parameters
 * Connection status and environment are now stored as Parameters, not simple XML
 */
export function extractQuickBooksConnectionStatus(connectionInfo: ERPConnectionInfo | null): {
    isConnected: boolean;
    connectedEnvironment?: string;
} | null {
    if (!connectionInfo) {
        return null;
    }

    const params = convertRawParametersToParams(connectionInfo.root?.Parameters?.Parameter ?? []);
    const paramMap = new Map(params.map((p) => [p.Name.toLowerCase(), p.Value || p.Default || '']));
    const qboConnectedValue = paramMap.get(QUICKBOOKS_PARAM_NAMES.CONNECTED) || '';
    const isConnected = qboConnectedValue.toLowerCase() === 'true';
    const environmentValue = paramMap.get(QUICKBOOKS_PARAM_NAMES.ENVIRONMENT) || '';

    // Map environment string to enum value
    let connectedEnvironment: string | undefined;
    if (environmentValue) {
        connectedEnvironment = environmentValue; // Use the value as-is from the parameter
    }

    return {
        isConnected,
        connectedEnvironment,
    };
}

/**
 * Extract non-Parameter fields from ERPConnectionInfo root
 * This captures backend-managed fields like LastReads, BackendField, etc.
 */
export function extractNonParameterFields(connectionInfo: ERPConnectionInfo | null): Record<string, any> {
    if (!connectionInfo?.root) {
        return {};
    }

    // Extract everything from root except Parameters
    const { Parameters, ...nonParameterFields } = connectionInfo.root;
    return nonParameterFields;
}

/**
 * Merge Parameters with preserved non-parameter fields
 * This is the surgical approach to preserve backend XML while updating parameters
 */
export function mergeParametersWithPreservedFields(
    params: ERPConnectionParam[],
    preservedFields: Record<string, any>
): ERPConnectionInfo {
    // Convert params to the expected XML structure
    const parameterArray = params.map((param) => {
        let value: string | null = null;
        if (param.Value !== undefined && param.Value !== null) {
            value = String(param.Value);
        } else if (param.Value === null) {
            value = null;
        }
        return {
            Name: param.Name ?? '',
            Label: param.Label ?? '',
            Description: param.Description ?? '',
            Type: param.Type,
            Default: param.Default ?? '',
            Value: value,
            EnumOptions: param.EnumOptions,
            Required: param.Required ?? '',
        };
    });

    // Merge preserved fields with new Parameters
    return {
        root: {
            Parameters: {
                Parameter: parameterArray,
            },
            ...preservedFields, // Add back LastReads, etc.
        },
    };
}
